import { createTooltipItems } from "../tooltips/highchartTooltip";
import { getDateString, getStartOfYearFromDateStrng } from "common/date-utils";
import { formatPointLabel } from "components/widgets/chart/base/utils";
import { getDataGranularity, getLeafValue } from "hooks/widget-data/datapoints-api/helper";
import { language } from "language/default";
import { DataGranularity } from "store/current-dashboard/types";
var MarkerTypeEnum;
(function (MarkerTypeEnum) {
    MarkerTypeEnum["currentValue"] = "currentValue";
    MarkerTypeEnum["minValue"] = "minValue";
})(MarkerTypeEnum || (MarkerTypeEnum = {}));
const NUMBER_OF_CHART_COLORS = 10;
const validateSingleValueParam = (serie) => serie.id && serie.type;
const isRightField = (key, field, fallback) => {
    if (field) {
        return field === key;
    }
    return fallback === key;
};
const generateTooltipItems = (measure, dimension, series, tooltip) => {
    // We should do this for all 44 timeseries but valueFormat comes from 4 different sources (widgetProps, selectedValues, series, yAxis) and fixing it is out of scope of this bug
    if (series.field) {
        const currency = measure?.currency;
        const value = formatPointLabel(getLeafValue(measure), series.valueFormat, "", { currency });
        const additional = tooltip?.map((t) => ({ label: t.label, value: formatPointLabel(getLeafValue(t.value), series.valueFormat, "", { currency }) })) ?? [];
        return createTooltipItems([
            { label: language.en.chart.timeBasedSeries.tooltip.referenceDate, value: getDateString(new Date(dimension)) },
            { label: series.name, value },
            ...additional
        ]);
    }
    return undefined;
};
const isNumber = (measure) => {
    if (!measure || Number.isFinite(measure)) {
        return true;
    }
    const leaf = getLeafValue(measure);
    if (!leaf || Number.isFinite(leaf)) {
        return true;
    }
    return false;
};
const getDimension = (element, dataGranularity) => (element?.dimension && dataGranularity === DataGranularity.yearly
    ? getStartOfYearFromDateStrng(element.dimension)
    : element?.dimension);
export const getMinValues = (root, dataGranularity) => {
    const measures = {};
    root.forEach((item) => {
        const itemMeasures = item.measures;
        const { dimension } = item;
        Object.entries(itemMeasures || []).forEach(([key, v]) => {
            const value = v;
            if (!measures[key] || value < measures[key][0]) {
                measures[key] = [value, getDimension(item, dataGranularity)];
            }
            else if (value === measures[key][0] && dimension > measures[key][1]) {
                measures[key][1] = getDimension(item, dataGranularity);
            }
        });
    });
    return measures;
};
const applyMarker = (point, { serie, asOfDate, originalDimension, minValue, measure, dimension, key }) => {
    serie.markers?.forEach((marker) => {
        if (marker === MarkerTypeEnum.currentValue) {
            if (asOfDate?.getTime() === originalDimension) {
                Object.assign(point, { marker: { enabled: true } });
            }
        }
        if (marker === MarkerTypeEnum.minValue && minValue && dimension === minValue[key][1]) {
            if (measure === minValue[key][0]) {
                Object.assign(point, { marker: { enabled: true } });
            }
        }
    });
};
const mapSingleValue = ({ originalDimension, dimension, measure, key, serie, currentPto, asOfDate, minValue, tooltip }) => {
    if (validateSingleValueParam(serie)) {
        if (isRightField(key, serie.field, serie.id) && !currentPto && Number.isFinite(dimension) && isNumber(measure)) {
            const point = {
                custom: {
                    referenceDate: originalDimension,
                    tooltip: generateTooltipItems(measure, originalDimension, serie, tooltip)
                },
                x: dimension,
                y: getLeafValue(measure)
            };
            if (serie.reversed && point.y) {
                point.y *= -1;
            }
            if (point.y) {
                serie.data?.push(point);
            }
            applyMarker(point, { asOfDate, dimension, key, measure, minValue, originalDimension, serie });
        }
    }
    else {
        throw new Error(`${JSON.stringify(serie)} is missing required params`);
    }
};
const validateAreaRangeParam = (serie) => serie.low && serie.high && serie.type;
const mapAreaRange = ({ originalDimension, dimension, measure, key, serie, currentPto }) => {
    if (validateAreaRangeParam(serie)) {
        if ((key === serie.low || key === serie.high) && isNumber(measure)) {
            let actualPto = currentPto;
            if (!actualPto && Number.isFinite(dimension)) {
                actualPto = {
                    custom: { referenceDate: originalDimension },
                    x: dimension
                };
                serie.data?.push(actualPto);
            }
            if (actualPto && serie.low === key) {
                actualPto.low = measure;
            }
            if (actualPto && serie.high === key) {
                actualPto.high = measure;
            }
        }
    }
    else {
        throw new Error(`${JSON.stringify(serie)} is missing required params`);
    }
};
var SerieTypeEnum;
(function (SerieTypeEnum) {
    SerieTypeEnum["arearange"] = "arearange";
    SerieTypeEnum["column"] = "column";
    SerieTypeEnum["line"] = "line";
})(SerieTypeEnum || (SerieTypeEnum = {}));
const timeSeriesMapper = {
    [SerieTypeEnum.arearange]: mapAreaRange,
    [SerieTypeEnum.column]: mapSingleValue,
    [SerieTypeEnum.line]: mapSingleValue
};
const mapYAxis = (params) => {
    const axis = params.filter((p) => p.type === "yaxis");
    const y2 = { opposite: true, softMax: 1 };
    if (axis.length) {
        return axis.map((y) => (y.type === "yaxis"
            ? {
                ...y.yAxis === 1 ? y2 : { endOnTick: y?.endOnTick ?? false },
                ...y.limits,
                limits: y.limits,
                name: y.name,
                reversed: y.reversed,
                valueFormat: y.valueFormat,
                valueUnit: y.valueUnit
            }
            : {}));
    }
    return params.reduce((acc, cur) => {
        acc[cur.yAxis] = { ...cur.yAxis === 0 ? {} : y2, max: cur.max, valueFormat: cur.valueFormat };
        return acc;
    }, []);
};
const mapAdditionalOptions = (params) => {
    const options = {};
    const plot = params?.filter((x) => x.type === "plotOptions")?.[0];
    if (plot) {
        options.plotOptions = plot?.options;
    }
    return options;
};
const getGranularityFromRequest = (request, element) => {
    if (request?.requests?.[0]?.measures?.[0]?.name && element.measures) {
        return getDataGranularity(Object.keys(element.measures)
            .map((m) => request.requests[0].measures?.filter((r) => r.name === m)?.[0]?.timeseries)
            .filter((x) => x)?.[0]);
    }
    return undefined;
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const highchartsTimeSeries = (params) => (args) => {
    const element = args.visitable;
    if (args.context.staticData.customResult?.length) {
        if (args.context.staticData.root[0] && !args.context.staticData.root[0]?.additionalData?.yaxis) {
            args.context.staticData.root[0].additionalData = {
                yaxis: mapYAxis(args.context.staticData.customResult),
                ...mapAdditionalOptions(args.context.staticData.customResult)
            };
            args.context.staticData.customResult = args.context.staticData.customResult?.filter((x) => x.type !== "yaxis" && x.type !== "plotOptions");
        }
        const dimension = getDimension(element, args.context.widgetState?.dataGranularity ?? getGranularityFromRequest(args.context.request, element));
        const minValue = getMinValues(args.context.staticData.root, args.context.widgetState?.dataGranularity);
        Object.entries(element.measures || {}).forEach(([key, measure]) => {
            args.context.staticData.customResult?.forEach((serie, i) => {
                if (serie.order === undefined) {
                    serie.colorIndex = i % NUMBER_OF_CHART_COLORS;
                }
                else {
                    serie.colorIndex = serie.order;
                }
                serie.data = serie.data ?? [];
                serie.custom = { monochrome: serie.monochrome, striped: serie.striped };
                timeSeriesMapper[serie.type]({
                    asOfDate: args.context.request?.asOfDate,
                    currentPto: serie.data?.find((pto) => pto.x === dimension),
                    dimension,
                    key,
                    measure,
                    minValue,
                    originalDimension: element?.dimension,
                    serie,
                    tooltip: serie.tooltip?.map((t) => ({ label: t.label, value: element.measures?.[t.value] }))
                });
            });
        });
    }
};
export const customCategoriesTimeseries = (params) => (args) => {
    const { categoryDatapoint, measureDatapoint } = params;
    const element = args.visitable;
    if (args.context.staticData.root[0] && !args.context.staticData.root[0]?.additionalData?.xaxis) {
        args.context.staticData.root[0].additionalData = {
            xaxis: {
                categories: [],
                title: { text: params.xAxisTitle },
                type: "category"
            }
        };
    }
    args.context.staticData.customResult?.forEach((s, i) => {
        if (s.id !== element.dimension) {
            return;
        }
        if (!s.colorIndex) {
            s.colorIndex = i % NUMBER_OF_CHART_COLORS;
        }
        const customCategory = element.measures?.[categoryDatapoint];
        if (!args.context.staticData.root[0].additionalData?.xaxis.categories.includes(customCategory)) {
            args.context.staticData.root[0].additionalData?.xaxis.categories.push(customCategory);
        }
        s.data = s.data ?? [];
        s.data.pushIfNotExist({
            name: customCategory,
            y: element.measures?.[measureDatapoint]
        }, (point) => point?.name === customCategory);
    });
};
const getDatapointFromMeasure = (element, measure, dimension) => ({
    name: element.measures?.[dimension],
    y: element.measures?.[measure]
});
const setStaticSeriesItem = (element, categoryDatapoint, measureDatapoint, customResult, legendName) => {
    const measureDatapointName = legendName ?? measureDatapoint;
    const isDatapointAlreadyExist = customResult?.find((c) => c.id === measureDatapoint);
    if (isDatapointAlreadyExist) {
        if (isDatapointAlreadyExist.data) {
            isDatapointAlreadyExist.data.pushIfNotExist(getDatapointFromMeasure(element, measureDatapoint, categoryDatapoint), (point) => point?.name === element.measures?.[categoryDatapoint]);
        }
        else {
            isDatapointAlreadyExist.data = [getDatapointFromMeasure(element, measureDatapoint, categoryDatapoint)];
        }
    }
    else {
        customResult?.unshift({
            colorIndex: 0,
            data: [getDatapointFromMeasure(element, measureDatapoint, categoryDatapoint)],
            id: measureDatapoint,
            name: measureDatapointName,
            type: "line",
            valueFormat: 3,
            yAxis: 0
        });
    }
};
export const staticCategoriesTimeseries = (params) => (args) => {
    const { categoryDatapoint, measureDatapoint, legendName } = params;
    const element = args.visitable;
    if (element.measures && element.measures?.[measureDatapoint] !== undefined) {
        if (categoryDatapoint && measureDatapoint) {
            setStaticSeriesItem(element, categoryDatapoint, measureDatapoint, args.context.staticData.customResult, legendName);
            const categoryValue = element.measures?.[categoryDatapoint];
            if (!args.context.staticData.root[0].additionalData?.xaxis.categories.includes(categoryValue)) {
                args.context.staticData.root[0].additionalData?.xaxis.categories.push(categoryValue);
            }
        }
    }
};
